/**
 * Dashboard+ — Client Moments.
 *
 * Birthdays + anniversaries within the next 7 days, sorted by daysAway
 * ascending. Each row → one-click WhatsApp link with a pre-written
 * greeting so the agent can wish the client without context-switching.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Cake, Heart, MessageCircle } from 'lucide-react';

const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }); }
  catch { return ''; }
};

const ClientMoments = ({ moments = [] }) => (
  <Card className="p-4 border-slate-200" data-testid="client-moments">
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        <Cake className="h-4 w-4 text-rose-500" />
        <h3 className="text-sm font-semibold text-slate-900">Client moments — next 7 days</h3>
      </div>
      <span className="text-[10px] text-slate-400">{moments.length}</span>
    </div>
    {moments.length === 0 ? (
      <div className="text-xs text-slate-400 py-4 text-center">No birthdays or anniversaries this week.</div>
    ) : (
      <ul className="divide-y divide-slate-100">
        {moments.slice(0, 8).map((m) => <MomentRow key={`${m.ucc}-${m.kind}`} m={m} />)}
      </ul>
    )}
  </Card>
);

const MomentRow = ({ m }) => {
  const Icon = m.kind === 'anniversary' ? Heart : Cake;
  const color = m.kind === 'anniversary' ? 'text-rose-600' : 'text-amber-600';
  // Null-guard: backend may omit `name` on rare rows (e.g. clients without a
  // populated full name). Fall back to a friendly generic greeting so the
  // WhatsApp message + row render never throw on `m.name.split is not a fn`.
  const firstName = (m.name && typeof m.name === 'string') ? m.name.split(' ')[0] : 'there';
  const displayName = m.name || 'Client';
  const waText = m.kind === 'anniversary'
    ? `Happy anniversary, ${firstName}! Wishing you both a wonderful year ahead.`
    : `Happy birthday, ${firstName}! Wishing you a fantastic year ahead.`;
  return (
    <li className="flex items-center gap-3 py-2" data-testid={`moment-${m.ucc}-${m.kind}`}>
      <Icon className={`h-4 w-4 ${color}`} />
      <div className="flex-1 min-w-0">
        <Link to={`/clients/${m.ucc}`} className="text-sm text-slate-900 hover:text-emerald-700 truncate block">{displayName}</Link>
        <div className="text-[10px] text-slate-400 capitalize">
          {m.kind} · {fmtDate(m.date)}{m.daysAway === 0 ? ' (today)' : ` · in ${m.daysAway} day${m.daysAway === 1 ? '' : 's'}`}
        </div>
      </div>
      <a
        href={`https://wa.me/?text=${encodeURIComponent(waText)}`}
        target="_blank" rel="noopener noreferrer"
        data-testid={`moment-wa-${m.ucc}-${m.kind}`}
        className="text-xs text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1"
      >
        <MessageCircle className="h-3 w-3" /> Wish
      </a>
    </li>
  );
};

export default ClientMoments;
